
import { useAuth } from "@/hooks/useAuth"
import { supabase } from "@/integrations/supabase/client"
import { useQuery } from "@tanstack/react-query"

interface UseAssessmentsOptions {
  page?: number
  pageSize?: number
}

export const useAssessments = ({ page = 1, pageSize = 20 }: UseAssessmentsOptions = {}) => {
  const { user } = useAuth()

  return useQuery({
    queryKey: ["assessments", user?.id, page, pageSize],
    queryFn: async () => {
      if (!user?.id) return []

      const isProvider = user.user_metadata?.is_provider === true

      if (isProvider) {
        // For providers: Get assessments that have any unread patient messages with pagination
        const offset = (page - 1) * pageSize
        
        const { data: unreadPatientMessages, error: messagesError } = await supabase
          .from("messages")
          .select("assessment_id")
          .eq("is_provider", false) // Messages from patients
          .is("read_at", null) // That are unread
          .order("created_at", { ascending: false })
          .range(offset, offset + pageSize * 10 - 1) // Get more messages to ensure we have enough unique assessment IDs

        if (messagesError) {
          console.error("Error fetching unread patient messages:", messagesError)
          return []
        }

        if (!unreadPatientMessages || unreadPatientMessages.length === 0) {
          return []
        }

        // Get unique assessment IDs that have unread patient messages
        const uniqueAssessmentIds = [...new Set(unreadPatientMessages.map(msg => msg.assessment_id))]
        
        // Take only the number we need for this page
        const paginatedAssessmentIds = uniqueAssessmentIds.slice(0, pageSize)

        // Now fetch the assessment details for these IDs
        const { data, error } = await supabase
          .from("assessments")
          .select(`
            *, 
            profiles(
              id,
              first_name,
              last_name,
              email,
              is_provider,
              utm_source,
              utm_medium,
              utm_campaign,
              utm_term,
              utm_content,
              created_at,
              updated_at
            )
          `)
          .in('id', paginatedAssessmentIds)
          .or('status.eq.completed,status.eq.prescribed')
          .order("created_at", { ascending: false })

        if (error) {
          console.error("Error fetching assessment details:", error)
          return []
        }

        return data || []
        
      } else {
        // For patients: Get their own assessments
        const { data, error } = await supabase
          .from("assessments")
          .select(`
            *, 
            profiles(
              id,
              first_name,
              last_name,
              email,
              is_provider,
              utm_source,
              utm_medium,
              utm_campaign,
              utm_term,
              utm_content,
              created_at,
              updated_at
            )
          `)
          .eq("user_id", user.id)
          .order("created_at", { ascending: false })
          .range((page - 1) * pageSize, page * pageSize - 1)

        if (error) {
          console.error("Error fetching assessments:", error)
          return []
        }

        return data || []
      }
    },
    enabled: !!user?.id,
  })
}

// Hook to get total count for pagination
export const useAssessmentsCount = () => {
  const { user } = useAuth()

  return useQuery({
    queryKey: ["assessments-count", user?.id],
    queryFn: async () => {
      if (!user?.id) return 0

      const isProvider = user.user_metadata?.is_provider === true

      if (isProvider) {
        // For providers: Count unique assessments with unread patient messages
        const { data: unreadPatientMessages, error } = await supabase
          .from("messages")
          .select("assessment_id")
          .eq("is_provider", false)
          .is("read_at", null)

        if (error) {
          console.error("Error counting unread patient messages:", error)
          return 0
        }

        // Get unique assessment IDs
        const uniqueAssessmentIds = [...new Set(unreadPatientMessages?.map(msg => msg.assessment_id) || [])]
        return uniqueAssessmentIds.length
        
      } else {
        // For patients: Count their own assessments
        const { count, error } = await supabase
          .from("assessments")
          .select("*", { count: "exact", head: true })
          .eq("user_id", user.id)

        if (error) {
          console.error("Error counting assessments:", error)
          return 0
        }

        return count || 0
      }
    },
    enabled: !!user?.id,
  })
}
